import { useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router";

const COOKIE_NAME = "na_onsite_access";

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function setCookie(name: string, value: string, expires: Date) {
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

function checkCookie(customerId: string, validTokens: string[], expiry: Date): boolean {
  if (new Date() > expiry) return false;
  const cookie = getCookie(COOKIE_NAME);
  if (!cookie) return false;
  const parts = cookie.split(":");
  const id = parts[0];
  const token = parts[1];
  return !!id && !!token && id === customerId && validTokens.includes(token);
}

export function useAccessControl(customerId: string, validTokens: string[], expiry: Date) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasAccess, setHasAccess] = useState(() =>
    checkCookie(customerId, validTokens, expiry),
  );

  const checkAndSetToken = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("access");
    if (token && validTokens.includes(token)) {
      setCookie(COOKIE_NAME, `${customerId}:${token}`, expiry);
      setHasAccess(true);
      navigate(location.pathname, { replace: true });
      return true;
    }
    return false;
  }, [customerId, validTokens, expiry, location, navigate]);

  return { hasAccess, checkAndSetToken };
}
